/**
 * UnitRateHelp.js — graphical help guide for Unit Rate, narrated by the
 * portal-wide AudioGuide (components/common/AudioGuide.js).
 *
 *   HELP_SECTIONS      — the guide content: each section carries rich JSX for
 *                        the modal AND a curated plain-english `speech` script
 *                        (nicer narration than DOM auto-extraction).
 *   UnitRateHelpModal  — the help window: "listen" call-to-action, step
 *                        pipeline diagram, per-section play buttons, and live
 *                        highlight + auto-scroll of the section being read.
 *
 * The floating mini-player is rendered globally by AudioGuideProvider in
 * App.js, so narration keeps playing after this modal closes — and even
 * if the user navigates to another page.
 */

import React, { useEffect, useRef } from 'react';
import { useAudioGuide } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

const ACCENT = '#0f766e';
const GUIDE_ID = 'unit-rate-help';

/* ------------------------------------------------------------------ */
/* Guide content                                                       */
/* ------------------------------------------------------------------ */

const Chip = ({ children, color = ACCENT }) => (
  <span style={{
    display: 'inline-block', padding: '2px 9px', margin: '2px 4px 2px 0',
    background: `${color}12`, color, border: `1px solid ${color}33`,
    borderRadius: 999, fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap',
  }}>{children}</span>
);

const Formula = ({ children }) => (
  <code style={{
    display: 'inline-block', padding: '3px 10px', margin: '3px 6px 3px 0',
    background: '#0f172a', color: '#7dd3fc', borderRadius: 7,
    fontSize: '0.74rem', fontFamily: 'ui-monospace, Consolas, monospace',
  }}>{children}</code>
);

const P = ({ children, style }) => (
  <p style={{ margin: '0 0 .6rem', lineHeight: 1.65, ...style }}>{children}</p>
);

export const HELP_SECTIONS = [
  {
    id: 'overview',
    icon: '🧮',
    color: ACCENT,
    title: 'What is Unit Rate?',
    speech: 'Welcome to Unit Rate. This tool is a digital version of the L G L Fabrication Combined Hourly Rate spreadsheet. Each rate sheet you save is one pricing scenario, for one client, one job, and one shift pattern. It builds a fully loaded hourly selling rate, starting from crew wages, and layering on indirect costs, supervision, materials, subcontractors, rentals, room and board, and finally your markups. Everything recalculates instantly as you type, just like the spreadsheet, but it is saved centrally, shareable with the team, and every change is audit logged.',
    body: (
      <>
        <P>A digital twin of the <strong>LGL Fabrication Combined Hourly Rate Template</strong>. Each saved
          <strong> rate sheet</strong> is one scenario — a client, a job, a shift pattern — that builds a fully-loaded
          <strong> combined hourly selling rate</strong>.</P>
        <div>
          <Chip>⚡ Live recalculation</Chip>
          <Chip color="#6366f1">💾 Saved & shareable</Chip>
          <Chip color="#ec4899">📜 Audit-logged</Chip>
          <Chip color="#f59e0b">📤 Excel export</Chip>
          <Chip color="#10b981">⧉ Duplicate scenarios</Chip>
        </div>
      </>
    ),
  },
  {
    id: 'shifts',
    icon: '⏱️',
    color: '#f59e0b',
    title: 'Step 1 — Weekly Shifts',
    speech: 'Step one, weekly shifts. Enter the shift hours and working days for the crew, and separately for supervision and support. From those, the sheet works out straight time and overtime hours per week. Straight time is the shift hours times the days, capped at forty hours. Anything beyond a four day week spills into overtime. These two numbers drive every blended rate on the sheet, so this is always the first thing to set.',
    body: (
      <>
        <P>Set shift hours × working days for the <strong>crew</strong> and for <strong>supervision &amp; support</strong>.
          The sheet derives weekly straight-time and overtime hours — these drive every blended rate below.</P>
        <Formula>ST = min(shift × days, 40)</Formula>
        <Formula>OT = max(shift × (days − 4), 0)</Formula>
      </>
    ),
  },
  {
    id: 'direct',
    icon: '🔧',
    color: '#0ea5e9',
    title: 'Step 2 — Direct Labour',
    speech: 'Step two, direct labour. List each trade on the crew, for example pipefitter welders, metal trades, and apprentices, with their base wage and overtime wage. The sheet blends the two wages over the straight time and overtime hours. Then each trade gets a percent labour, which is its share of the crew mix. The rate component is the blended wage times that share, and the total across all trades is your direct labour cost per hour. Tip, the percent labour column should normally add up to about one hundred percent.',
    body: (
      <>
        <P>Each trade's base + OT wage is <strong>blended</strong> over the ST/OT hours, then weighted by its
          <strong> % Labour</strong> share of the crew mix. The total is your direct labour cost per hour.</P>
        <Formula>Blended = (Base×ST + OT rate×OT) ÷ (ST+OT)</Formula>
        <Formula>Component = % Labour × Blended</Formula>
        <P style={{ margin: 0 }}><Chip color="#0ea5e9">💡 % Labour should sum to ≈100%</Chip></P>
      </>
    ),
  },
  {
    id: 'other-direct',
    icon: '📦',
    color: '#6366f1',
    title: 'Step 3 — Other Direct',
    speech: 'Step three, other direct costs. These are percentage add ons applied to the direct labour total. Things like material handling, welder qualification, and rework. Items such as job setup and safety orientation are usually only needed on jobs with over thirty trades people. Keep a line at zero percent to leave it documented on the sheet without affecting the rate.',
    body: (
      <>
        <P>Percentage add-ons applied to the <strong>direct total</strong> — material handling, welder qualification,
          rework, job setup, safety orientation…</P>
        <Formula>Component = % Labour × Direct total</Formula>
        <P style={{ margin: 0 }}><Chip color="#6366f1">💡 Keep a line at 0% to document it without charging it</Chip></P>
      </>
    ),
  },
  {
    id: 'staff',
    icon: '👷',
    color: '#ec4899',
    title: 'Step 4 — Supervision & Support',
    speech: 'Step four, supervision and support staff. Foremen, general foremen, quality control, cost clerks, project managers, and so on. Their wages are blended over the supervision shift, weighted by percent labour, and grossed up by their own other direct percentage. The equivalent hours column shows how many staff hours that percentage implies against the total direct labour hours, which is a great sanity check that your staffing assumptions are realistic.',
    body: (
      <>
        <P>Foremen, GF, QC, PM, safety, document control… blended over the supervision shift, weighted by
          % labour and grossed up by their own <strong>Other Direct %</strong>.</P>
        <Formula>Component = Blended × (%Labour × (1 + OtherDir%))</Formula>
        <Formula>Equiv. hours = Total direct hrs × % Labour</Formula>
        <P style={{ margin: 0 }}><Chip color="#ec4899">💡 Equivalent hours = staffing sanity check</Chip></P>
      </>
    ),
  },
  {
    id: 'msr',
    icon: '🚚',
    color: '#f59e0b',
    title: 'Step 5 — Materials, Subs & Rentals',
    speech: 'Step five, materials, subcontractors, and rentals. Materials, like consumables, are priced as a percentage of the loaded labour, meaning direct plus other direct. Subs and rentals each carry their own hourly rate, and you choose where that rate comes from. Type it manually, pull it from the X ray calculation, which divides total X ray cost by total hours, or link it to an equipment line, where the cost per hour is the weekly rate times percent use divided by the working week hours. Linked lines update automatically whenever the source changes.',
    body: (
      <>
        <P><strong>Materials</strong> = % of loaded labour (direct + other direct). <strong>Subs / Rentals</strong> carry
          their own $/hr from one of three sources:</P>
        <div style={{ marginBottom: '.5rem' }}>
          <Chip color="#64748b">⌨️ Manual entry</Chip>
          <Chip color="#10b981">☢️ X-ray calc: cost ÷ hours</Chip>
          <Chip color="#14b8a6">🚜 Equipment link</Chip>
        </div>
        <Formula>Equipment $/hr = Weekly × %Use ÷ Week hours</Formula>
        <P style={{ margin: 0 }}><Chip color="#f59e0b">💡 Linked lines follow their source automatically</Chip></P>
      </>
    ),
  },
  {
    id: 'room-board',
    icon: '🏨',
    color: '#64748b',
    title: 'Step 6 — Room & Board',
    speech: 'Step six, room and board. For out of town work, add hourly allowances for craft room and board, and travel. Each line is a dollars per hour rate times a percent labour. Leave the percentages at zero for local work, and the lines stay on the sheet as a reminder without adding cost.',
    body: (
      <>
        <P>Hourly allowances for out-of-town work — craft R&amp;B and travel.</P>
        <Formula>Component = $/hour × % Labour</Formula>
      </>
    ),
  },
  {
    id: 'summary',
    icon: '🧾',
    color: ACCENT,
    title: 'Step 7 — Summary, Markups & Selling Rate',
    speech: 'Step seven, the summary. Every cost group rolls up here, and each gets its own markup. Labour groups typically carry twenty six percent, while materials, subs, and rentals usually carry ten percent. The selling rate for each group is its cost times one plus the markup. Finally, the payment treatment time percentage grosses the total up to cover slow payment terms, giving you the final combined hourly selling rate. The charts beside the summary show exactly where every dollar of the rate comes from.',
    body: (
      <>
        <P>Every group rolls up with its own markup — labour typically <strong>26%</strong>,
          materials/subs/rentals <strong>10%</strong>. Payment terms gross-up the final rate.</P>
        <Formula>Selling = Cost × (1 + M/U)</Formula>
        <Formula>Final = Total × (1 + Payment terms %)</Formula>
        <P style={{ margin: 0 }}><Chip>📊 The donut & bar charts show where every dollar goes</Chip></P>
      </>
    ),
  },
  {
    id: 'sheets',
    icon: '💾',
    color: '#8b5cf6',
    title: 'Working with sheets',
    speech: 'Working with sheets. New rate sheet starts you from the L G L fabrication template with sensible defaults. Duplicate copies an existing sheet so you can price a variation, like a different shift pattern or markup, without touching the original. Export downloads the whole calculation as an Excel file. Nothing is stored until you press save. An asterisk on the save button means you have unsaved changes. And every create, save, and delete is recorded in the audit log. That is the whole tour. Build a sheet, watch the selling rate update live, and save your scenario. Happy estimating!',
    body: (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8 }}>
          {[
            ['➕ New Rate Sheet', 'starts from the LGL fabrication template'],
            ['⧉ Duplicate', 'copy a sheet to price a variation safely'],
            ['📤 Export', 'download the whole calc as Excel'],
            ['💾 Save*', 'asterisk = unsaved changes'],
            ['📜 Audit Log', 'every create / save / delete recorded'],
            ['☢️ X-ray hint', 'welds × req% × cost each = suggested total'],
          ].map(([t, d]) => (
            <div key={t} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '8px 11px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#334155' }}>{t}</div>
              <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>{d}</div>
            </div>
          ))}
        </div>
      </>
    ),
  },
];

const GUIDE_SECTIONS = HELP_SECTIONS.map((s) => ({ title: s.title, icon: s.icon, speech: s.speech }));

/* ------------------------------------------------------------------ */
/* Help modal                                                          */
/* ------------------------------------------------------------------ */

const PIPELINE = [
  { icon: '⏱️', label: 'Shifts', color: '#f59e0b' },
  { icon: '🔧', label: 'Direct', color: '#0ea5e9' },
  { icon: '📦', label: 'Other Dir.', color: '#6366f1' },
  { icon: '👷', label: 'Supv.', color: '#ec4899' },
  { icon: '🚚', label: 'Mat/Subs', color: '#f59e0b' },
  { icon: '🏨', label: 'R&B', color: '#64748b' },
  { icon: '🧾', label: 'Markups', color: ACCENT },
  { icon: '💰', label: 'Rate', color: '#7c3aed' },
];

export const UnitRateHelpModal = ({ open, onClose }) => {
  const g = useAudioGuide();
  const sectionRefs = useRef({});

  const isThisGuide = g.activeGuideId === GUIDE_ID;
  const listening = isThisGuide && (g.status === 'playing' || g.status === 'paused');
  const playing = isThisGuide && g.status === 'playing';

  const startFrom = (sectionIdx = 0) => g.startGuide(
    { id: GUIDE_ID, title: 'Unit Rate — Guide', accent: ACCENT, sections: GUIDE_SECTIONS },
    { fromSectionIdx: sectionIdx },
  );
  const onHeroClick = () => {
    if (playing) g.pause();
    else if (isThisGuide && g.status === 'paused') g.play();
    else startFrom(0);
  };
  const onSectionPlay = (i) => {
    if (isThisGuide && g.currentSectionIdx === i && playing) g.pause();
    else if (isThisGuide) g.playSection(i);
    else startFrom(i);
  };

  // Auto-scroll the modal to the section being narrated.
  useEffect(() => {
    if (!open || !isThisGuide || g.status !== 'playing') return;
    const el = sectionRefs.current[g.currentSectionIdx];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [open, isThisGuide, g.status, g.currentSectionIdx]);

  if (!open) return null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(2px)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#f8fafc', borderRadius: 16, maxWidth: 860, width: '100%', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}>

        {/* Hero header */}
        <div style={{ position: 'sticky', top: 0, zIndex: 2, background: `linear-gradient(135deg, ${ACCENT} 0%, #115e59 60%, #134e4a 100%)`, color: 'white', padding: '1.1rem 1.5rem 1rem', borderRadius: '16px 16px 0 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                🧮 Unit Rate — Guide
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', opacity: 0.85 }}>
                How a fully-loaded hourly selling rate gets built, step by step.
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, borderRadius: '50%', width: 32, height: 32 }}>×</button>
          </div>

          {/* Listen call-to-action */}
          {g.supported && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, background: 'rgba(255,255,255,0.12)', borderRadius: 12, padding: '8px 12px' }}>
              <button
                onClick={onHeroClick}
                style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'white', color: ACCENT, fontSize: '1rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.25)', flexShrink: 0 }}
              >
                {playing ? '⏸' : '▶'}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>🎧 Listen to this guide</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                  Audio keeps playing after you close this window — a mini-player stays on the page while you work.
                </div>
              </div>
              <button onClick={g.toggleGender}
                style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: 'white', cursor: 'pointer', borderRadius: 999, padding: '5px 12px', fontSize: '0.72rem', fontWeight: 700 }}>
                {g.gender === 'female' ? '👩 Female voice' : '👨 Male voice'}
              </button>
            </div>
          )}
        </div>

        <HelpWithVideoTour guideId={GUIDE_ID} title="Unit Rate — Guide" accent={ACCENT}>
        {/* Pipeline diagram */}
        <div style={{ padding: '1rem 1.5rem 0.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center', background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
            {PIPELINE.map((p, i) => (
              <React.Fragment key={p.label}>
                <div style={{ textAlign: 'center', minWidth: 58 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%', margin: '0 auto 3px',
                    background: `${p.color}14`, border: `2px solid ${p.color}55`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
                  }}>{p.icon}</div>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>{p.label}</div>
                </div>
                {i < PIPELINE.length - 1 && <div style={{ color: '#cbd5e1', fontWeight: 700, margin: '0 1px 14px' }}>→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Section cards */}
        <div style={{ padding: '0.9rem 1.5rem 1.4rem', display: 'grid', gap: 12 }}>
          {HELP_SECTIONS.map((s, i) => {
            const isCurrent = listening && g.currentSectionIdx === i;
            return (
              <div
                key={s.id}
                ref={(el) => { sectionRefs.current[i] = el; }}
                style={{
                  background: 'white', borderRadius: 12, padding: '0.9rem 1.1rem',
                  border: isCurrent ? `2px solid ${s.color}` : '1px solid #e2e8f0',
                  borderLeft: `5px solid ${s.color}`,
                  boxShadow: isCurrent ? `0 0 0 4px ${s.color}22, 0 4px 14px rgba(0,0,0,0.08)` : '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'box-shadow .25s, border-color .25s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                    background: `${s.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem',
                  }}>{s.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', flex: 1 }}>{s.title}</div>
                  {g.supported && (
                    <button
                      onClick={() => onSectionPlay(i)}
                      title="Listen from here"
                      style={{
                        width: 28, height: 28, borderRadius: '50%', border: `1px solid ${s.color}44`,
                        background: isCurrent && playing ? s.color : `${s.color}10`,
                        color: isCurrent && playing ? 'white' : s.color,
                        cursor: 'pointer', fontSize: '0.68rem', flexShrink: 0,
                      }}
                    >{isCurrent && playing ? '🔊' : '▶'}</button>
                  )}
                </div>
                <div style={{ fontSize: '0.83rem', color: '#374151' }}>{s.body}</div>
              </div>
            );
          })}
        </div>
        </HelpWithVideoTour>
      </div>
    </div>
  );
};
